import React from 'react';
import { colors } from 'spw-theme';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { showToastMessage } from 'spw-redux/modules/UI/messages/actions';
import { selectMeasurementSystem } from 'spw-redux/modules/user/selectors';
import { changeMeasurementSystem } from 'spw-redux/modules/user/actions';
import { MeasurementSystem } from './types';

const ToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  z-index: 1300;
  margin-left: 24px;
`;

const Label = styled.span`
  font-size: 12px;
  color: ${colors.black};
  font-weight: 500;
`;

const ToggleContainer = styled.div`
  background-color: ${colors.veryLightGray}80;
  border: 1px solid ${colors.veryLightGray};
  border-radius: 9999px;
  display: inline-flex;
  padding: 0px;
  position: relative;
  width: 62px;
  height: 24px;
`;

const ToggleOption = styled.button<{ isSelected: boolean }>`
  background: none;
  border: none;
  border-radius: 9999px;
  color: ${(props) => (props.isSelected ? colors.black : colors.disabledIcons)};
  cursor: pointer;
  font-weight: 500;
  padding: 0;
  position: relative;
  z-index: 1;
  transition: color 0.3s ease;
  width: 50%;
  height: 100%;
  font-size: 12px;
`;

const SelectionIndicator = styled.div<{ selectedOption: MeasurementSystem }>`
  background-color: ${colors.brandBlue}26;
  border: 1px solid ${colors.brandBlue};
  border-radius: 9999px;
  box-sizing: border-box;
  height: calc(100% + 2px);
  position: absolute;
  top: -1px;
  transition: left 0.3s ease;
  width: calc(50% + 1px);
  left: ${(props) =>
    props.selectedOption === MeasurementSystem.imperial
      ? '-1px'
      : 'calc(50% + 0px)'};
`;

export const UnitToggle = ({
  onToggle,
  system,
  shouldHideLabel,
}: {
  system: MeasurementSystem;
  onToggle: (newSystem: MeasurementSystem) => void;
  shouldHideLabel?: boolean;
}) => {
  const handleToggle = () => {
    const newUnit =
      system === MeasurementSystem.imperial
        ? MeasurementSystem.metric
        : MeasurementSystem.imperial;
    if (onToggle) {
      onToggle(newUnit);
    }
  };

  return (
    <ToggleWrapper>
      {shouldHideLabel ? null : <Label>UNIT</Label>}
      <ToggleContainer
        onClick={(e) => {
          e.stopPropagation();
          handleToggle();
        }}
      >
        <SelectionIndicator selectedOption={system} />
        <ToggleOption
          isSelected={system === MeasurementSystem.imperial}
          aria-label="feet"
        >
          FT
        </ToggleOption>
        <ToggleOption
          isSelected={system === MeasurementSystem.metric}
          aria-label="meters"
        >
          M
        </ToggleOption>
      </ToggleContainer>
    </ToggleWrapper>
  );
};

export default function UnitConversionToggle({
  shouldHideLabel = false,
}: {
  shouldHideLabel?: boolean;
}) {
  const dispatch = useDispatch();
  const system = useSelector(selectMeasurementSystem);

  const handleToggle = (newUnit: MeasurementSystem) => {
    dispatch(changeMeasurementSystem(newUnit));
    dispatch(
      showToastMessage({
        text: `You're now working with ${newUnit} units.`,
        status: 'success',
      })
    );
  };

  return (
    <UnitToggle
      system={system}
      onToggle={handleToggle}
      shouldHideLabel={shouldHideLabel}
    />
  );
}
